"use client";

import { useEffect, useRef, useState } from "react";
import { Heading } from "@/components/ui/typography";
import Image from "next/image";

type Slide = {
  num: string;
  title: string;
  image: string;
};

const SLIDES: Slide[] = [
  {
    num: "01",
    title: "Bank selection guidance matched to your structure",
    image: "/slide-banking.webp",
  },
  {
    num: "02",
    title: "Application preparation in approval-ready format",
    image: "/slide-banking.webp",
  },
  {
    num: "03",
    title: "KYC documentation structure",
    image: "/slide-banking.webp",
  },
  {
    num: "04",
    title: "Business activity explanation drafting",
    image: "/slide-banking.webp",
  },
  {
    num: "05",
    title: "Ongoing post-opening support",
    image: "/slide-banking.webp",
  },
];

export default function HKBankingSlider({ heading = "What We Handle Inside Your HK Banking Setup" }: { heading?: string } = {}) {
  const [active, setActive] = useState(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      const triggerPoint = window.innerHeight / 2;

      let closestIdx = 0;
      let closestDist = Infinity;

      itemRefs.current.forEach((el, i) => {
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - triggerPoint);

        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });

      setActive(closestIdx);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative bg-black">

      {/* HEADER */}
      <div className="max-w-3xl mx-auto px-4 text-center pt-24 pb-[60px]">
        <Heading as="h2" variant="sectionCentered">
          {heading}
        </Heading>

        <p className="mt-6 text-white/70 text-[14px] md:text-[16px]">
          Opening an account successfully requires more than submitting a form.
          We support your entire application process.
        </p>
      </div>

      {/* MAIN WRAPPER */}
      <div className="relative max-w-[1200px] mx-auto flex">

        {/* LEFT CONTENT */}
        <div className="w-full lg:w-1/2 px-4 relative">

          {/* 🔵 VERTICAL PAGINATION */}
          <div className="hidden lg:flex flex-col items-center absolute left-[-40px] top-1/2 -translate-y-1/2 space-y-6">
            {SLIDES.map((_, i) => {
              const isActive = active === i;

              return (
                <div
                  key={i}
                  className="relative flex items-center justify-center"
                >
                  {/* Line connector */}
                  {i !== SLIDES.length - 1 && (
                    <div className="absolute top-6 w-[2px] h-6 bg-white/10" />
                  )}

                  {/* Dot */}
                  <div
                    className={`w-3.5 h-3.5 rounded-full transition-all duration-300
                      ${
                        isActive
                          ? "bg-white scale-125"
                          : "bg-white/20"
                      }`}
                  />
                </div>
              );
            })}
          </div>

          {/* SCROLL CONTENT */}
          <div className="space-y-[24px] py-0">
            {SLIDES.map((s, i) => {
              const isActive = active === i;

              return (
                <div
                  key={s.num}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  className="max-w-[480px]"
                >
                  <div
                    className={`w-14 h-14 border-white/10 rounded-full opacity-30 bg-[#0f1218] shadow-[5px_5px_0_0_rgba(255,255,255,0.15)] flex items-center justify-center text-xs font-bold transition-all duration-500
                      ${
                        isActive
                          ? "border-2 border-white bg-[#0f1218] shadow-[5px_5px_0_0_rgba(255,255,255,0.15)] text-white"
                          : "border-white/10 text-white/40"
                      }`}
                  >
                    {s.num}
                  </div>

                  <h3
                    className={`mt-4 text-[22px] md:text-[28px] leading-[120%] transition-all duration-500
                      ${
                        isActive
                          ? "text-white font-semibold"
                          : "text-white/40"
                      }`}
                  >
                    {s.title}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div className="hidden lg:block w-1/2 relative">
          <div className="sticky top-[120px] w-full h-[420px]">

            <div className="relative w-full h-full rounded-3xl overflow-hidden border border-white/10 shadow-xl">
              {SLIDES.map((s, i) => (
                <div
                  key={s.num}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    active === i ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <Image
                    src={s.image}
                    alt={s.title}
                    fill
                    className="object-cover"
                  />
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}